import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Upload, AlertCircle, CheckCircle, Database, Search, Trash2, Server, FileText, RefreshCw, Eye, Clock, Activity, CloudUpload, History } from "lucide-react";
import { bustImageCache } from "../../utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DataExportImport = ({ token }) => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingMedia, setImportingMedia] = useState(false);
  const [message, setMessage] = useState("");
  const [importResults, setImportResults] = useState(null);
  const [mediaImportResults, setMediaImportResults] = useState(null);
  const [detectingOrphans, setDetectingOrphans] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [orphanResults, setOrphanResults] = useState(null);
  const [cleanupResults, setCleanupResults] = useState(null);

  // System info state
  const [systemInfo, setSystemInfo] = useState(null);
  const [loadingSystemInfo, setLoadingSystemInfo] = useState(true);

  // Import preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Activity log state
  const [logEntries, setLogEntries] = useState([]);
  const [logDates, setLogDates] = useState([]);
  const [selectedLogDate, setSelectedLogDate] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  // Backup state
  const [backups, setBackups] = useState([]);
  const [lastBackup, setLastBackup] = useState(null);
  const [backupSchedule, setBackupSchedule] = useState('');
  const [backupError, setBackupError] = useState('');
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(null);

  // Fetch system info on mount
  useEffect(() => {
    fetchSystemInfo();
    fetchBackups();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      setLoadingSystemInfo(true);
      const response = await axios.get(`${API}/admin/system-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemInfo(response.data);
    } catch (error) {
      console.error('Error fetching system info:', error);
    } finally {
      setLoadingSystemInfo(false);
    }
  };

  // Backup functions
  const fetchBackups = async () => {
    try {
      setLoadingBackups(true);
      setBackupError('');
      const response = await axios.get(`${API}/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
      setLastBackup(response.data.last_backup);
      setBackupSchedule(response.data.schedule || '');
      if (response.data.error) setBackupError(response.data.error);
    } catch (error) {
      console.error('Error fetching backups:', error);
      setBackupError('Failed to connect to backup storage');
    } finally {
      setLoadingBackups(false);
    }
  };

  const runBackupNow = async () => {
    if (!confirm("Run a manual backup now? This may take a moment.")) return;
    try {
      setRunningBackup(true);
      setMessage("");
      const response = await axios.post(`${API}/admin/backups/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(`Backup created: ${response.data.backup?.filename}`);
      fetchBackups();
      fetchSystemInfo();
    } catch (err) {
      setMessage(`Backup failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRunningBackup(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      setDownloadingBackup(filename);
      const response = await axios.get(`${API}/admin/backups/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setMessage(`Download failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDownloadingBackup(null);
    }
  };

  // Activity log functions
  const fetchLogDates = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity-logs/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogDates(response.data.dates || []);
      if (response.data.dates?.length > 0) {
        setSelectedLogDate(response.data.dates[0]);
        fetchLogs(response.data.dates[0]);
      }
    } catch (error) {
      console.error('Error fetching log dates:', error);
    }
  };

  const fetchLogs = async (date) => {
    try {
      setLoadingLogs(true);
      const response = await axios.get(`${API}/admin/activity-logs?date=${date}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const downloadLog = async () => {
    try {
      const url = selectedLogDate
        ? `${API}/admin/activity-logs/download?date=${selectedLogDate}`
        : `${API}/admin/activity-logs/download`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `activity_${selectedLogDate || 'latest'}.jsonl`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setMessage("Failed to download activity log");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setMessage("");
      
      const response = await axios.get(`${API}/admin/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header
      const disposition = response.headers['content-disposition'];
      let filename = 'georgedives-export.json';
      if (disposition) {
        const match = disposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setMessage("Data exported successfully! Check your downloads.");
      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      console.error("Export failed:", err);
      setMessage(`Export failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Import preview
  const handleImportPreview = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setMessage("Please select a JSON file");
      event.target.value = null;
      return;
    }

    try {
      setLoadingPreview(true);
      setPreviewData(null);
      setImportResults(null);
      setPreviewFile(file);

      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/import-preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreviewData(response.data);
    } catch (err) {
      console.error("Preview failed:", err);
      setMessage(`Preview failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingPreview(false);
      event.target.value = null;
    }
  };

  // Execute import after preview
  const handleConfirmImport = async () => {
    if (!previewFile) return;
    
    const wipeMsg = previewData?.wipe_all
      ? "WIPE ALL is enabled. ALL existing data will be ERASED and replaced. Continue?"
      : "This will import data alongside existing data. Some collections will be replaced. Continue?";
    
    if (!confirm(wipeMsg)) return;

    try {
      setImporting(true);
      setMessage("");

      const formData = new FormData();
      formData.append('file', previewFile);
      
      const response = await axios.post(`${API}/admin/import-data`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data.results);
      bustImageCache();
      const wipePerformed = response.data.results?.wipe_all_performed;
      setMessage(wipePerformed 
        ? "Clean import completed! All previous data was wiped and replaced." 
        : "Data imported successfully!");
      setPreviewData(null);
      setPreviewFile(null);
      fetchSystemInfo();
    } catch (err) {
      console.error("Import failed:", err);
      setMessage(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleMediaOnlyImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setMessage("Please select a JSON file");
      return;
    }
    if (!confirm("Import preset media files only? (Database won't be touched)")) {
      event.target.value = null;
      return;
    }
    try {
      setImportingMedia(true);
      setMessage("");
      setMediaImportResults(null);
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/import-preset-media`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setMediaImportResults(response.data.results);
      setMessage("Preset media imported successfully!");
    } catch (err) {
      setMessage(`Media import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImportingMedia(false);
      event.target.value = null;
    }
  };

  const handleDetectOrphans = async () => {
    try {
      setDetectingOrphans(true);
      setOrphanResults(null);
      setCleanupResults(null);
      const response = await axios.get(`${API}/admin/detect-orphans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrphanResults(response.data);
    } catch (err) {
      setMessage(`Detection failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDetectingOrphans(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!confirm("Remove broken image references and delete orphaned files?")) return;
    try {
      setCleaningOrphans(true);
      setCleanupResults(null);
      const response = await axios.post(`${API}/admin/cleanup-orphans`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanupResults(response.data);
      bustImageCache();
      setMessage("Cleanup complete!");
    } catch (err) {
      setMessage(`Cleanup failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCleaningOrphans(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    try {
      return new Date(iso).toLocaleString();
    } catch { return iso; }
  };

  const statusColor = (status) => {
    if (status >= 500) return 'text-red-600 bg-red-50';
    if (status >= 400) return 'text-yellow-700 bg-yellow-50';
    return 'text-green-700 bg-green-50';
  };

  return (
    <div className="space-y-6">
      {/* ============ SYSTEM INFO PANEL ============ */}
      <Card className="border-2 border-slate-300 bg-slate-50" data-testid="system-info-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
              <Server className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <CardTitle className="text-2xl">System Info</CardTitle>
              <CardDescription>Current app state and version tracking</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-auto" 
              onClick={fetchSystemInfo}
              data-testid="refresh-system-info-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loadingSystemInfo ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSystemInfo && !systemInfo ? (
            <div className="text-center py-4 text-sm text-slate-500">Loading system info...</div>
          ) : systemInfo ? (
            <div className="space-y-4">
              {/* Version badges row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="app-version-badge">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">App Version</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">v{systemInfo.app_version}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="db-version-badge">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">DB Version</p>
                  <p className="text-2xl font-bold text-cyan-700 mt-1">#{systemInfo.db_version}</p>
                  <p className="text-xs text-slate-400 mt-1">Increments on each import</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="last-import-badge">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Last Import</p>
                  {systemInfo.last_import ? (
                    <>
                      <p className="text-sm font-semibold text-slate-700 mt-1">
                        {formatDate(systemInfo.last_import.date)}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Source: {systemInfo.last_import.source_version || 'N/A'}
                        {systemInfo.last_import.wipe_all && (
                          <span className="ml-1 text-red-500 font-medium">(wipe-all)</span>
                        )}
                      </p>
                      {systemInfo.last_import.filename && (
                        <p className="text-xs text-slate-400 truncate" title={systemInfo.last_import.filename}>
                          File: {systemInfo.last_import.filename}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-400 mt-1">No imports yet</p>
                  )}
                </div>
              </div>

              {/* Collection counts summary */}
              {systemInfo.collections && (
                <details className="bg-white rounded-lg border border-slate-200 p-3">
                  <summary className="text-sm font-medium text-slate-600 cursor-pointer">
                    Database Collections ({Object.keys(systemInfo.collections).length} collections, {Object.values(systemInfo.collections).reduce((a, b) => a + b, 0)} total documents)
                  </summary>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                    {Object.entries(systemInfo.collections).map(([name, count]) => (
                      <div key={name} className="text-xs bg-slate-50 rounded px-2 py-1.5 flex justify-between">
                        <span className="text-slate-600 truncate mr-2">{name}</span>
                        <span className="font-mono font-semibold text-slate-800">{count}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <p className="text-sm text-red-500">Failed to load system info</p>
          )}
        </CardContent>
      </Card>

      {/* ============ EXPORT SECTION ============ */}
      <Card className="border-2 border-blue-200" data-testid="export-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Export Data</CardTitle>
              <CardDescription>
                Download all data as a versioned JSON file
                {systemInfo && (
                  <span className="ml-1 text-blue-600">
                    (will be named georgedives_v{systemInfo.app_version}_db{systemInfo.db_version}_[date].json)
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
            data-testid="export-btn"
          >
            {exporting ? (
              <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Exporting...</>
            ) : (
              <><Download className="w-5 h-5 mr-2" /> Export All Data</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* ============ IMPORT WITH PREVIEW ============ */}
      <Card className="border-2 border-green-200" data-testid="import-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Upload className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Import Data (with Preview)</CardTitle>
              <CardDescription>
                Upload a JSON to preview changes before committing
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Step 1: Select file for preview */}
            {!previewData && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportPreview}
                  disabled={loadingPreview}
                  className="hidden"
                  id="import-preview-file"
                  data-testid="import-preview-input"
                />
                <label htmlFor="import-preview-file" className={`cursor-pointer ${loadingPreview ? 'opacity-50' : ''}`}>
                  {loadingPreview ? (
                    <div className="flex flex-col items-center gap-3">
                      <RefreshCw className="w-12 h-12 text-green-600 animate-spin" />
                      <p className="text-lg font-semibold text-gray-700">Analyzing file...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <Eye className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">Select JSON to Preview</p>
                        <p className="text-sm text-gray-500 mt-1">We'll show you what will change before importing</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            )}

            {/* Step 2: Preview diff */}
            {previewData && !importResults && (
              <div className="space-y-4" data-testid="import-preview-panel">
                {/* Source info */}
                <div className={`rounded-lg p-4 border-2 ${previewData.wipe_all ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5" />
                    <span className="font-semibold">
                      {previewData.wipe_all ? 'WIPE-ALL Import' : 'Merge Import'}
                    </span>
                  </div>
                  <div className="text-sm grid grid-cols-2 gap-2">
                    <div>Source version: <strong>{previewData.source_version}</strong></div>
                    <div>Source date: <strong>{formatDate(previewData.source_date)}</strong></div>
                  </div>
                  {previewData.wipe_all && (
                    <p className="text-sm text-red-700 mt-2 font-medium">
                      All existing collections will be ERASED before importing.
                    </p>
                  )}
                </div>

                {/* Diff table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="grid grid-cols-3 gap-0 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-2 border-b">
                    <div>Collection</div>
                    <div className="text-center">Current DB</div>
                    <div className="text-center">Incoming</div>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {previewData.diff
                      .filter(d => d.incoming > 0 || d.current > 0)
                      .map((d, idx) => {
                        const willReplace = previewData.wipe_all;
                        const gaining = d.incoming > d.current;
                        const losing = d.incoming < d.current && willReplace;
                        return (
                          <div key={idx} className="grid grid-cols-3 gap-0 px-3 py-1.5 text-sm items-center hover:bg-slate-50">
                            <div className="text-slate-700 truncate">{d.collection}</div>
                            <div className="text-center font-mono text-slate-600">{d.current}</div>
                            <div className={`text-center font-mono font-semibold ${
                              losing ? 'text-red-600' : gaining ? 'text-green-600' : 'text-slate-600'
                            }`}>
                              {d.incoming}
                              {willReplace && d.incoming !== d.current && (
                                <span className="ml-1 text-xs">
                                  ({d.incoming - d.current > 0 ? '+' : ''}{d.incoming - d.current})
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setPreviewData(null); setPreviewFile(null); }}
                    className="flex-1"
                    data-testid="cancel-import-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmImport}
                    disabled={importing}
                    className={`flex-1 text-white ${previewData.wipe_all ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                    data-testid="confirm-import-btn"
                  >
                    {importing ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> {previewData.wipe_all ? 'Wipe & Import' : 'Import'}</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preset Media Only Import */}
      <Card className="border-2 border-purple-200" data-testid="media-import-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Upload className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Import Preset Media Only</CardTitle>
              <CardDescription>Add preset media files without touching database</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
            <input type="file" accept=".json" onChange={handleMediaOnlyImport} disabled={importingMedia} className="hidden" id="import-media-file" />
            <label htmlFor="import-media-file" className={`cursor-pointer ${importingMedia ? 'opacity-50' : ''}`}>
              {importingMedia ? (
                <div className="flex flex-col items-center gap-2">
                  <RefreshCw className="w-10 h-10 text-purple-600 animate-spin" />
                  <p className="font-semibold text-gray-700">Importing Media...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-purple-400" />
                  <p className="font-semibold text-gray-900">Media only import</p>
                  <p className="text-xs text-gray-500">Safe for live site - DB untouched</p>
                </div>
              )}
            </label>
          </div>
        </CardContent>
      </Card>

      {/* ============ STATUS MESSAGES ============ */}
      {message && (
        <div className={`p-4 rounded-lg border-2 ${
          message.toLowerCase().includes('fail') || message.toLowerCase().includes('error')
            ? 'bg-red-50 border-red-300 text-red-800' 
            : 'bg-green-50 border-green-300 text-green-800'
        }`} data-testid="status-message">
          {message}
        </div>
      )}

      {/* Media Import Results */}
      {mediaImportResults && (
        <Card className="border-2 border-purple-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <CheckCircle className="w-6 h-6" /> Preset Media Import Complete!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-600">Imported</p>
                <p className="text-3xl font-bold text-green-600">{mediaImportResults.imported || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-600">Skipped</p>
                <p className="text-3xl font-bold text-yellow-600">{mediaImportResults.skipped || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-600">Errors</p>
                <p className="text-3xl font-bold text-red-600">{mediaImportResults.errors?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Import Results */}
      {importResults && (
        <Card className="border-2 border-green-300" data-testid="import-results-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-6 h-6" />
              Import Successful!
              {importResults.wipe_all_performed && <span className="text-sm font-normal text-red-500 ml-2">(DB was wiped)</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Admin Accounts', importResults.admin_users],
                ['All Users', importResults.all_users],
                ['Dive Centers', importResults.dive_centers],
                ['Dive Sites', importResults.dive_sites],
                ['Packages', importResults.packages],
                ['Timeline', importResults.timeline],
                ['Reviews', importResults.reviews],
                ['Dive Reports', importResults.dive_reports],
                ['Marine Species', importResults.marine_species],
                ['Page Styles', importResults.page_styles],
                ['Preset Media', importResults.preset_media],
                ['Chat Messages', importResults.chat_messages],
              ].filter(([, v]) => v > 0).map(([label, value]) => (
                <div key={label} className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-600">{label}</p>
                  <p className="text-2xl font-bold text-green-600">{value}</p>
                </div>
              ))}
            </div>
            {importResults.errors?.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-red-800">Errors:</p>
                <ul className="text-sm text-red-700 ml-4 list-disc">
                  {importResults.errors.map((err, idx) => <li key={idx}>{err}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ CLOUD BACKUPS ============ */}
      <Card className="border-2 border-teal-200" data-testid="cloud-backups-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <CloudUpload className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Cloud Backups</CardTitle>
              <CardDescription>
                {backupSchedule && <span>Schedule: <strong>{backupSchedule}</strong></span>}
                {' '}&mdash; Stored in Emergent Object Storage
              </CardDescription>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchBackups} data-testid="refresh-backups-btn">
                <RefreshCw className={`w-4 h-4 ${loadingBackups ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                onClick={runBackupNow}
                disabled={runningBackup}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="run-backup-now-btn"
              >
                {runningBackup ? (
                  <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Running...</>
                ) : (
                  <><CloudUpload className="w-4 h-4 mr-1" /> Backup Now</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {backupError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              {backupError}
            </div>
          )}

          {/* Last backup summary */}
          {lastBackup && (
            <div className="bg-teal-50 rounded-lg p-3 mb-4 border border-teal-200">
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Last Backup</p>
              <p className="text-sm font-semibold text-teal-800 mt-1">{lastBackup.filename}</p>
              <p className="text-xs text-teal-600">
                {formatDate(lastBackup.created_at)} &mdash; {lastBackup.size_mb} MB &mdash; v{lastBackup.app_version} db#{lastBackup.db_version}
              </p>
            </div>
          )}

          {/* Backup history */}
          {loadingBackups ? (
            <div className="text-center py-6 text-sm text-slate-500">Loading backups...</div>
          ) : backups.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No backups yet. Click "Backup Now" to create the first one.
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-12 gap-0 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-2 border-b">
                <div className="col-span-5">Filename</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-1 text-center">Size</div>
                <div className="col-span-1 text-center">App</div>
                <div className="col-span-1 text-center">DB</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {backups.map((b, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-0 px-3 py-2 text-sm items-center hover:bg-slate-50">
                    <div className="col-span-5 text-slate-700 truncate font-mono text-xs" title={b.filename}>
                      {b.filename}
                    </div>
                    <div className="col-span-2 text-slate-500 text-xs">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}
                    </div>
                    <div className="col-span-1 text-center text-xs text-slate-500">
                      {b.size_bytes ? `${(b.size_bytes / 1024 / 1024).toFixed(1)}MB` : '-'}
                    </div>
                    <div className="col-span-1 text-center text-xs text-slate-500">v{b.app_version}</div>
                    <div className="col-span-1 text-center text-xs text-slate-500">#{b.db_version}</div>
                    <div className="col-span-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadBackup(b.filename)}
                        disabled={downloadingBackup === b.filename}
                        data-testid={`download-backup-${idx}`}
                      >
                        {downloadingBackup === b.filename ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Download className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ ACTIVITY LOGS ============ */}
      <Card className="border-2 border-indigo-200" data-testid="activity-logs-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Activity Logs</CardTitle>
              <CardDescription>API request history for debugging</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => {
                if (!showLogs) { setShowLogs(true); fetchLogDates(); }
                else setShowLogs(false);
              }}
              data-testid="toggle-activity-logs-btn"
            >
              {showLogs ? 'Hide' : 'Show Logs'}
            </Button>
          </div>
        </CardHeader>
        {showLogs && (
          <CardContent>
            <div className="space-y-4">
              {/* Date picker + download */}
              <div className="flex items-center gap-3">
                <select
                  value={selectedLogDate || ''}
                  onChange={(e) => { setSelectedLogDate(e.target.value); fetchLogs(e.target.value); }}
                  className="flex-1 h-10 px-3 border border-gray-300 rounded-md bg-white text-sm"
                  data-testid="log-date-select"
                >
                  {logDates.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <Button variant="outline" size="sm" onClick={() => fetchLogs(selectedLogDate)} data-testid="refresh-logs-btn">
                  <RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="outline" size="sm" onClick={downloadLog} data-testid="download-log-btn">
                  <Download className="w-4 h-4" />
                </Button>
              </div>

              {/* Log entries table */}
              {loadingLogs ? (
                <div className="text-center py-8 text-sm text-slate-500">Loading logs...</div>
              ) : logEntries.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-400">No log entries for this date</div>
              ) : (
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="grid grid-cols-12 gap-0 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-2 border-b">
                    <div className="col-span-2">Time</div>
                    <div className="col-span-1">Method</div>
                    <div className="col-span-5">Path</div>
                    <div className="col-span-1 text-center">Status</div>
                    <div className="col-span-1 text-right">ms</div>
                    <div className="col-span-2 text-right">User</div>
                  </div>
                  <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                    {logEntries.map((entry, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-0 px-3 py-1.5 text-xs items-center hover:bg-slate-50">
                        <div className="col-span-2 text-slate-500 font-mono">
                          {entry.ts ? new Date(entry.ts).toLocaleTimeString() : '-'}
                        </div>
                        <div className="col-span-1">
                          <span className={`font-semibold ${
                            entry.method === 'GET' ? 'text-green-600' :
                            entry.method === 'POST' ? 'text-blue-600' :
                            entry.method === 'PUT' ? 'text-yellow-600' :
                            entry.method === 'DELETE' ? 'text-red-600' : 'text-slate-600'
                          }`}>{entry.method}</span>
                        </div>
                        <div className="col-span-5 text-slate-700 truncate font-mono" title={entry.path}>
                          {entry.path}
                        </div>
                        <div className="col-span-1 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-mono font-semibold ${statusColor(entry.status)}`}>
                            {entry.status}
                          </span>
                        </div>
                        <div className="col-span-1 text-right font-mono text-slate-500">{entry.duration_ms}</div>
                        <div className="col-span-2 text-right text-slate-400 truncate">{entry.user || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* ============ DATA HEALTH CHECK ============ */}
      <Card className="border-2 border-orange-200" data-testid="orphan-detection-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Search className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Data Health Check</CardTitle>
              <CardDescription>Detect broken image references and orphaned files</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-3">
              <Button
                onClick={handleDetectOrphans}
                disabled={detectingOrphans}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="detect-orphans-btn"
              >
                {detectingOrphans ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Search className="w-4 h-4 mr-2" /> Detect Orphans</>}
              </Button>
              {orphanResults && (orphanResults.summary?.broken_profile_pics > 0 || orphanResults.summary?.broken_review_images > 0 || orphanResults.summary?.orphaned_files_on_disk > 0) && (
                <Button onClick={handleCleanupOrphans} disabled={cleaningOrphans} variant="destructive" className="flex-1" data-testid="cleanup-orphans-btn">
                  {cleaningOrphans ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Cleaning...</> : <><Trash2 className="w-4 h-4 mr-2" /> Clean Up All</>}
                </Button>
              )}
            </div>

            {orphanResults && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    ['Broken Profile Pics', orphanResults.summary?.broken_profile_pics],
                    ['Broken Review Images', orphanResults.summary?.broken_review_images],
                    ['Broken DC Images', orphanResults.summary?.broken_dive_center_images],
                    ['Orphaned Files', orphanResults.summary?.orphaned_files_on_disk]
                  ].map(([label, count]) => (
                    <div key={label} className="bg-white rounded-lg p-3 border">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className={`text-2xl font-bold ${count > 0 ? 'text-red-600' : 'text-green-600'}`}>{count || 0}</p>
                    </div>
                  ))}
                </div>

                {orphanResults.broken_profile_pics?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-red-800 mb-1">Broken Profile Pictures:</p>
                    <ul className="text-xs text-red-700 space-y-1">
                      {orphanResults.broken_profile_pics.map((item, i) => <li key={i}>{item.user_name} ({item.user_email}) - {item.filename}</li>)}
                    </ul>
                  </div>
                )}

                {!orphanResults.summary?.broken_profile_pics && !orphanResults.summary?.broken_review_images && !orphanResults.summary?.orphaned_files_on_disk && !orphanResults.summary?.broken_dive_center_images && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-green-800">All clean! No orphans detected.</p>
                  </div>
                )}
              </div>
            )}

            {cleanupResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-green-800 mb-2">Cleanup Results:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-700">{cleanupResults.profile_pics_cleared}</p>
                    <p className="text-xs text-gray-600">Profiles cleared</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-700">{cleanupResults.review_images_cleaned}</p>
                    <p className="text-xs text-gray-600">Review imgs cleaned</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-700">{cleanupResults.orphaned_files_deleted}</p>
                    <p className="text-xs text-gray-600">Files deleted</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataExportImport;
